/**
 * Panel de diagnóstico de la conexión con Twilio (SPEC-estadisticas-conexion §A.5 y §B.7.4).
 *
 * - Al abrir se pide el último diagnóstico (`GET`): el backend lo reevalúa contra la base sin
 *   llamar a Twilio. «Volver a revisar» corre uno en vivo (`POST`, uno cada 20 s por marca).
 * - Si quedan pasos `pendiente` (el evento de prueba que Twilio tiene que mandar), se pregunta de
 *   nuevo cada 3 s durante 60 s: el paso pasa a ok solo cuando llega el evento firmado.
 * - El semáforo sale de `resumenDiagnostico`: verde SÓLO si el backend dice «listo» y ningún
 *   paso bloqueante está en error (fail-closed si las dos fuentes no coinciden).
 * - Cada paso trae su arreglo: el botón lo decide `accionDePaso` (una acción mal formada, como un
 *   enlace que no es https, no se pinta).
 * - «Reanudar la marca» (el freno por cumplimiento que una prueba del Sandbox superó) es el mismo de la prueba y de la
 *   tarjeta de la marca frenada (`ReanudarTrasPrueba`); al terminar se vuelve a pedir el diagnóstico.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, CircleDashed, Clock, Copy, ExternalLink, Loader2, MinusCircle,
  RefreshCw, Stethoscope, Wand2, XCircle,
} from "lucide-react";
import type { ClaveConfirmacionManual, Diagnostico, PasoConexion, PasoDiagnostico, ResultadoAutoconfigurar } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { usePolling } from "../../hooks/usePolling";
import {
  accionDePaso, agruparPasos, detalleLegible, hayPendientes, iconoPaso, intervaloDiagnostico, pasoAConfirmar, resumenDiagnostico,
  textoErrorConexion,
} from "../../logic/conexion";
import type { AccionUI, IconoPaso } from "../../logic/conexion";
import { leerError, leerRespuesta } from "../../logic/respuesta";
import { Tarjeta } from "../Tarjeta";
import { ModalReemplazarEntrada } from "./ModalReemplazarEntrada";
import { ReanudarTrasPrueba } from "./ReanudarTrasPrueba";
import { fechaHora } from "../../../campana-nueva/utils/format";

// ── Piezas compartidas con el asistente ──────────────────────────────────────────────────────

const ICONOS: Record<IconoPaso, JSX.Element> = {
  ok: <CheckCircle2 size={16} />,
  error: <XCircle size={16} />,
  advertencia: <AlertTriangle size={16} />,
  pendiente: <Clock size={16} />,
  omitido: <MinusCircle size={16} />,
  no_aplica: <CircleDashed size={16} />,
  confirmar: <AlertTriangle size={16} />,
};

/** Ícono de un estado, con su texto para lectores de pantalla (el color solo no alcanza). */
export function IconoEstado({ estado }: { estado: string }) {
  const i = iconoPaso(estado);
  return (
    <span className={`inline-flex flex-shrink-0 ${i.cls}`} title={i.label}>
      {ICONOS[i.icono]}
      <span className="sr-only">{i.label}</span>
    </span>
  );
}

/** Lista de pasos de `conectar` o `autoconfigurar`, con su resultado. */
export function ListaPasosConexion({ pasos }: { pasos: PasoConexion[] }) {
  if (!pasos.length) return null;
  return (
    <ol className="space-y-2" aria-label="Resultado de la conexión">
      {pasos.map((p) => (
        <li key={p.id} className="flex items-start gap-2.5 text-sm">
          <span className="mt-0.5"><IconoEstado estado={p.estado} /></span>
          <span className="min-w-0">
            <span className="font-medium text-slate-800">{p.titulo || p.id}</span>
            {p.detalle && <span className="block text-xs text-slate-500 break-words">{p.detalle}</span>}
            {p.codigoTwilio && <span className="block text-xs text-slate-400">Código de Twilio {p.codigoTwilio}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * «Arreglarlo automáticamente» / «Reconfigurar en Twilio»: corre la auto-configuración y, si la
 * entrada del servicio es de otra aplicación, pide confirmar antes de pisarla (A1-10).
 */
export function useAutoconfigurar(alTerminar: (r: ResultadoAutoconfigurar) => void) {
  const { recargar } = useCuentaOficial();
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [pasos, setPasos] = useState<PasoConexion[] | null>(null);
  const [urlAConfirmar, setUrlAConfirmar] = useState<string | null>(null);
  const alTerminarRef = useRef(alTerminar);
  alTerminarRef.current = alTerminar;

  const correr = useCallback(async (reemplazarEntrada = false) => {
    setOcupado(true);
    setError("");
    try {
      const l = leerRespuesta<ResultadoAutoconfigurar>(await oficialApi.autoconfigurar(reemplazarEntrada));
      if (!l.ok) { setError(textoErrorConexion({ ...l, codigoTwilio: l.extra.codigoTwilio })); return; }
      if (!l.data || !Array.isArray(l.data.pasos)) { setError("El servidor no devolvió el resultado."); return; }
      setPasos(l.data.pasos);
      const aConfirmar = reemplazarEntrada ? null : pasoAConfirmar(l.data.pasos);
      setUrlAConfirmar(aConfirmar ? aConfirmar.urlAnterior || "" : null);
      if (l.data.diagnostico) alTerminarRef.current(l.data);
      await recargar();
    } catch (e) {
      const x = leerError(e);
      setError(textoErrorConexion({ ...x, codigoTwilio: x.body.codigoTwilio }));
    } finally {
      setOcupado(false);
    }
  }, [recargar]);

  return {
    ocupado, error, pasos, urlAConfirmar, correr,
    confirmar: () => { setUrlAConfirmar(null); void correr(true); },
    cancelar: () => setUrlAConfirmar(null),
  };
}

// ── El panel ─────────────────────────────────────────────────────────────────────────────────

export function PanelDiagnostico({ diagnostico, onDiagnostico, onCambiarToken }: {
  diagnostico: Diagnostico | null;
  onDiagnostico: (d: Diagnostico) => void;
  onCambiarToken: () => void;
}) {
  const { cuenta, recargar } = useCuentaOficial();
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [revisando, setRevisando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [pollingDesde, setPollingDesde] = useState<number | null>(null);
  const auto = useAutoconfigurar((r) => onDiagnostico(r.diagnostico));

  const traer = useCallback(async (): Promise<string> => {
    try {
      const l = leerRespuesta<Diagnostico>(await oficialApi.diagnostico());
      if (!l.ok) return textoErrorConexion(l);
      if (!l.data || !Array.isArray(l.data.pasos)) return "El servidor no devolvió el diagnóstico.";
      onDiagnostico(l.data);
      return "";
    } catch (e) {
      return textoErrorConexion(leerError(e));
    }
  }, [onDiagnostico]);

  // Primera carga: sólo si todavía no hay uno (el asistente pudo haber traído uno al conectar).
  const pedido = useRef(false);
  useEffect(() => {
    if (diagnostico || pedido.current) return;
    pedido.current = true;
    setCargando(true);
    void traer().then((e) => { setError(e); setCargando(false); });
  }, [diagnostico, traer]);

  const intervalo = intervaloDiagnostico(diagnostico, pollingDesde, Date.now());
  const polling = usePolling(async () => {
    const e = await traer();
    if (e) throw new Error(e);
  }, intervalo, [pollingDesde]);

  const revisar = async () => {
    if (revisando) return;
    setRevisando(true);
    setError("");
    setAviso("");
    try {
      const l = leerRespuesta<Diagnostico>(await oficialApi.revisarDiagnostico());
      if (!l.ok) { setError(textoErrorConexion(l)); return; }
      if (!l.data || !Array.isArray(l.data.pasos)) { setError("El servidor no devolvió el diagnóstico."); return; }
      onDiagnostico(l.data);
      // Twilio manda el evento de prueba en unos segundos: se pregunta de nuevo hasta 60 s.
      if (hayPendientes(l.data)) setPollingDesde(Date.now());
    } catch (e) {
      const x = leerError(e);
      if (x.status === 429 || x.code === "DEMASIADO_SEGUIDO") setAviso(textoErrorConexion({ code: "DEMASIADO_SEGUIDO" }));
      else setError(textoErrorConexion(x));
    } finally {
      setRevisando(false);
    }
  };

  const [guardandoClave, setGuardandoClave] = useState<ClaveConfirmacionManual | null>(null);
  const confirmarManual = async (clave: ClaveConfirmacionManual, valor: boolean) => {
    if (guardandoClave) return;
    setGuardandoClave(clave);
    setError("");
    try {
      // `portfolioVerificado` viaja en el nivel de la cuenta; las otras dos, dentro de config (A1-7).
      const cuerpo = clave === "portfolioVerificado" ? { portfolioVerificado: valor }
        : clave === "complianceConfirmado" ? { complianceConfirmado: valor } : { advancedOptOutConfirmado: valor };
      const l = leerRespuesta(await oficialApi.actualizarConfig(cuerpo));
      if (!l.ok) { setError(textoErrorConexion(l)); return; }
      await recargar();
      // El GET reevalúa los pasos de cumplimiento contra la base: la casilla se ve al instante.
      const e = await traer();
      if (e) setError(e);
    } catch (e) {
      setError(textoErrorConexion(leerError(e)));
    } finally {
      setGuardandoClave(null);
    }
  };

  const r = resumenDiagnostico(diagnostico);
  const grupos = agruparPasos(diagnostico);

  return (
    <Tarjeta titulo="Diagnóstico de la conexión" right={<Stethoscope size={16} className="text-slate-400" />}>
      <div className={`flex items-center gap-3 flex-wrap rounded-xl px-4 py-3 ${!diagnostico ? "bg-slate-50" : r.listo ? "bg-emerald-50" : "bg-red-50"}`}>
        <span className={`inline-flex items-center gap-2 text-sm font-semibold ${!diagnostico ? "text-slate-600" : r.listo ? "text-emerald-700" : "text-red-700"}`} role="status">
          {diagnostico ? (r.listo ? <CheckCircle2 size={18} /> : <XCircle size={18} />) : <CircleDashed size={18} />}
          {cargando && !diagnostico ? "Revisando la conexión…" : r.titulo}
        </span>
        {diagnostico?.at && <span className="text-xs text-slate-500">Revisado {fechaHora(diagnostico.at)}</span>}
        <button type="button" onClick={() => void revisar()} disabled={revisando}
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {revisando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Volver a revisar
        </button>
      </div>

      {intervalo !== null && (
        <p className="mt-2 text-xs text-sky-700 inline-flex items-center gap-1.5" role="status">
          <Loader2 size={12} className="animate-spin" /> Esperando el aviso de prueba de Twilio…
        </p>
      )}
      {polling.detenido && (
        <p className="mt-2 text-xs text-amber-800">
          Dejamos de preguntar porque el servidor no responde.{" "}
          <button type="button" onClick={polling.reintentar} className="font-semibold hover:underline">Reintentar</button>
        </p>
      )}
      {aviso && <p role="status" className="mt-3 text-sm text-amber-800">{aviso}</p>}
      {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
      {auto.error && <p role="alert" className="mt-3 text-sm text-red-600">{auto.error}</p>}
      {auto.pasos && (
        <div className="mt-4 rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Lo que hicimos en Twilio</p>
          <ListaPasosConexion pasos={auto.pasos} />
        </div>
      )}

      {cargando && !diagnostico ? (
        <div className="mt-4 space-y-2" aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-xl bg-slate-100 animate-pulse" />)}</div>
      ) : (
        <div className="mt-4 space-y-2">
          {grupos.map((g) => (
            <GrupoDiagnostico key={g.grupo} titulo={g.titulo} pasos={g.pasos} abiertoInicial={g.abierto} conteo={g.conteo}
              render={(p) => (
                <FilaPaso key={p.id} paso={p} accion={accionDePaso(p, cuenta)} ocupadoAuto={auto.ocupado}
                  guardandoClave={guardandoClave}
                  onAutoconfigurar={() => void auto.correr(false)}
                  onConfirmar={(clave, v) => void confirmarManual(clave, v)}
                  onCambiarToken={onCambiarToken}
                  onReanudada={() => void traer().then((e) => { if (e) setError(e); })} />
              )} />
          ))}
        </div>
      )}

      {auto.urlAConfirmar !== null && (
        <ModalReemplazarEntrada urlAnterior={auto.urlAConfirmar} busy={auto.ocupado} onConfirmar={auto.confirmar} onCancelar={auto.cancelar} />
      )}
    </Tarjeta>
  );
}

function GrupoDiagnostico({ titulo, pasos, abiertoInicial, conteo, render }: {
  titulo: string; pasos: PasoDiagnostico[]; abiertoInicial: boolean; conteo: Record<string, number>;
  render: (p: PasoDiagnostico) => JSX.Element;
}) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  // Si después de revisar aparece un problema en un grupo cerrado, se abre solo.
  useEffect(() => { if (abiertoInicial) setAbierto(true); }, [abiertoInicial]);
  const problemas = (conteo.error || 0) + (conteo.advertencia || 0) + (conteo.pendiente || 0);
  return (
    <section className="rounded-xl border border-slate-100">
      <button type="button" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50 rounded-xl">
        {abierto ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        {titulo}
        <span className={`ml-auto text-xs font-medium ${conteo.error ? "text-red-600" : problemas ? "text-amber-700" : "text-emerald-700"}`}>
          {conteo.error ? `${conteo.error} con error` : problemas ? `${problemas} para revisar` : "todo bien"}
        </span>
      </button>
      {abierto && <ul className="px-4 pb-3 space-y-3">{pasos.map(render)}</ul>}
    </section>
  );
}

function FilaPaso({ paso, accion, ocupadoAuto, guardandoClave, onAutoconfigurar, onConfirmar, onCambiarToken, onReanudada }: {
  paso: PasoDiagnostico; accion: AccionUI | null; ocupadoAuto: boolean; guardandoClave: ClaveConfirmacionManual | null;
  onAutoconfigurar: () => void; onConfirmar: (clave: ClaveConfirmacionManual, valor: boolean) => void; onCambiarToken: () => void;
  onReanudada: () => void;
}) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { /* sin portapapeles: el nombre igual está a la vista */ }
  };
  const mostrarArreglo = paso.arreglo && (paso.estado === "error" || paso.estado === "advertencia" || paso.estado === "pendiente");
  return (
    <li className="flex items-start gap-2.5 pt-2 border-t border-slate-50 first:border-t-0 first:pt-0">
      <span className="mt-0.5"><IconoEstado estado={paso.estado} /></span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium text-slate-800">
          {paso.titulo || paso.id}
          {paso.bloqueante && paso.estado === "error" && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">impide enviar</span>}
        </p>
        {paso.detalle && <p className="text-xs text-slate-600 break-words">{detalleLegible(paso.detalle)}</p>}
        {mostrarArreglo && <p className="text-xs text-slate-700"><span className="font-semibold">Cómo arreglarlo:</span> {paso.arreglo}</p>}
        {paso.codigoTwilio && <p className="text-xs text-slate-400">Código de Twilio {paso.codigoTwilio}</p>}
        {accion && accion.tipo !== "reanudar_marca" && (
          <div className="pt-1">
            {accion.tipo === "autoconfigurar" && (
              <button type="button" onClick={onAutoconfigurar} disabled={ocupadoAuto}
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                {ocupadoAuto ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} {accion.etiqueta}
              </button>
            )}
            {accion.tipo === "enlace" && (
              <a href={accion.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline">
                {accion.etiqueta} <ExternalLink size={12} />
              </a>
            )}
            {accion.tipo === "copiar" && (
              <span className="inline-flex items-center gap-2 text-xs text-slate-700">
                {accion.etiqueta}
                <button type="button" onClick={() => void copiar(accion.nombre)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-0.5 font-semibold hover:bg-slate-50">
                  <Copy size={12} /> {copiado ? "Copiado" : "Copiar"}
                </button>
              </span>
            )}
            {accion.tipo === "casilla" && (
              <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-800">
                <input type="checkbox" checked={accion.marcado} disabled={guardandoClave !== null}
                  onChange={(e) => onConfirmar(accion.clave, e.target.checked)}
                  className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                {accion.etiqueta}
                {guardandoClave === accion.clave && <Loader2 size={12} className="animate-spin text-slate-400" />}
              </label>
            )}
            {accion.tipo === "cambiar_token" && (
              <button type="button" onClick={onCambiarToken} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                {accion.etiqueta}
              </button>
            )}
            {accion.tipo === "navegar" && (
              <Link to={accion.ruta} className="text-xs font-semibold text-violet-700 hover:underline">{accion.etiqueta}</Link>
            )}
          </div>
        )}
        {/* Montado siempre (sin freno superado no pinta nada): así el «por qué no levantó» queda a la vista aunque el
            paso cambie al releer. */}
        <div className="pt-1 empty:hidden">
          <ReanudarTrasPrueba superado={accion?.tipo === "reanudar_marca" ? accion.superado : null} soloBoton alTerminar={onReanudada} />
        </div>
      </div>
    </li>
  );
}

export default PanelDiagnostico;
